import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { engagementsAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { RefreshCw, Plus, Trash2, Briefcase, Pencil, X, Eye } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const STATUSES = ['pipeline', 'proposal', 'active', 'delivered', 'invoiced', 'closed', 'lost'];
const TYPES = ['engineering_services', 'managed_part_supply', 'framework', 'subscription', 'other'];

const emptyForm = () => ({
  engagementId: '',
  title: '',
  type: 'engineering_services',
  status: 'pipeline',
  conflictCheckId: '',
  valueEur: '',
  notes: '',
  screenId: '',
  customerId: '',
});

const AdminEngagements = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await engagementsAPI.getAll({ limit: 100, status: statusFilter || undefined });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load engagements');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setShowForm(true); };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      engagementId: row.engagementId || '',
      title: row.title || '',
      type: row.type || 'engineering_services',
      status: row.status || 'pipeline',
      conflictCheckId: row.conflictCheckId || '',
      valueEur: row.valueEur != null ? String(row.valueEur) : '',
      notes: row.notes || '',
      screenId: typeof row.screenId === 'object' && row.screenId ? row.screenId._id : (row.screenId || ''),
      customerId: typeof row.customerId === 'object' && row.customerId ? row.customerId._id : (row.customerId || ''),
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    const body = {
      ...form,
      valueEur: form.valueEur === '' ? undefined : Number(form.valueEur),
      screenId: form.screenId || undefined,
      customerId: form.customerId || undefined,
    };
    try {
      if (editingId) {
        await engagementsAPI.update(editingId, body);
        toast.success('Engagement updated');
      } else {
        await engagementsAPI.create(body);
        toast.success('Engagement created');
      }
      setShowForm(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this engagement?')) return;
    try {
      await engagementsAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
  const money = (n) => (n != null ? `€${Number(n).toLocaleString('en-MT')}` : '—');

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Briefcase size={28} color="#eab308" /> Engagements
            </h1>
            <p style={{ color: '#a1a1aa' }}>
              Paid work from screens / inbound engineering (not print-shop orders).
              {total ? ` ${total} engagements.` : ' Empty until a screen converts.'}
            </p>
          </div>
          <div className="admin-header-right">
            <button type="button" onClick={openCreate} style={btn('#eab308')}><Plus size={15} /> New engagement</button>
            <button type="button" className="btn-secondary" onClick={load}><RefreshCw size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip active={!statusFilter} onClick={() => setStatusFilter('')} label="All" color="#eab308" />
          {STATUSES.map((s) => (
            <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={s} color="#eab308" />
          ))}
        </div>

        {showForm && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#fff' }}>{editingId ? 'Edit engagement' : 'New engagement'}</h3>
              <button type="button" onClick={() => setShowForm(false)} style={iconBtn}><X size={16} /></button>
            </div>
            <div style={grid}>
              <label style={field}><span style={muted}>Engagement ID</span>
                <input value={form.engagementId} onChange={(e) => setForm((p) => ({ ...p, engagementId: e.target.value }))} placeholder="ENG-2026-001" style={input} /></label>
              <label style={field}><span style={muted}>Title</span>
                <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Type</span>
                <select value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} style={input}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Status</span>
                <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} style={input}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Value (€)</span>
                <input type="number" value={form.valueEur} onChange={(e) => setForm((p) => ({ ...p, valueEur: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>COI conflict check ID</span>
                <input value={form.conflictCheckId} onChange={(e) => setForm((p) => ({ ...p, conflictCheckId: e.target.value }))} placeholder="links to compliance register" style={input} /></label>
              <label style={field}><span style={muted}>Screen ObjectId (optional)</span>
                <input value={form.screenId} onChange={(e) => setForm((p) => ({ ...p, screenId: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Customer ObjectId (optional)</span>
                <input value={form.customerId} onChange={(e) => setForm((p) => ({ ...p, customerId: e.target.value }))} style={input} /></label>
              <label style={{ ...field, gridColumn: '1 / -1' }}><span style={muted}>Notes</span>
                <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} style={input} /></label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={handleSave} style={btn('#eab308')}>Save</button>
              <button type="button" onClick={() => setShowForm(false)} style={ghost}>Cancel</button>
            </div>
          </div>
        )}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr style={thead}>
                {['ID', 'Title', 'Type', 'Status', 'Value', 'COI check', 'Created', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={emptyCell}>Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} style={emptyCell}>
                  No engagements yet. Convert an obsolescence screen or log inbound engineering work here.
                </td></tr>
              ) : items.map((row) => (
                <tr key={row._id} style={rowStyle}>
                  <td style={td}><strong>{row.engagementId || row._id.slice(-6)}</strong></td>
                  <td style={td}>{row.title || '—'}</td>
                  <td style={td}>{row.type || '—'}</td>
                  <td style={td}><span style={pill('#eab308')}>{row.status}</span></td>
                  <td style={td}>{money(row.valueEur)}</td>
                  <td style={td}>{row.conflictCheckId || '—'}</td>
                  <td style={td}>{fmt(row.createdAt)}</td>
                  <td style={td}>
                    <button type="button" onClick={() => navigate(`/admin/engagements/${row._id}`)} style={iconBtn} title="Detail"><Eye size={14} /></button>
                    <button type="button" onClick={() => openEdit(row)} style={iconBtn}><Pencil size={14} /></button>
                    <button type="button" onClick={() => handleDelete(row._id)} style={iconBtn}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

function Chip({ active, onClick, label, color }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
      border: active ? `2px solid ${color}` : '1px solid #27272a',
      background: active ? `${color}20` : '#18181b',
      color: active ? color : '#a1a1aa',
      fontWeight: active ? 600 : 400,
    }}>{label}</button>
  );
}

const btn = (c) => ({ background: c, border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 });
const ghost = { background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' };
const iconBtn = { background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: 6 };
const card = { background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24, marginBottom: 24 };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const muted = { color: '#71717a' };
const input = { background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 };
const tableWrap = { background: '#111', borderRadius: 12, overflow: 'hidden', border: '1px solid #27272a' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const thead = { background: '#18181b', borderBottom: '1px solid #27272a' };
const th = { padding: '12px 16px', color: '#71717a', fontWeight: 600, textAlign: 'left' };
const td = { padding: '14px 16px', color: '#fff' };
const rowStyle = { borderBottom: '1px solid #1c1c1c' };
const emptyCell = { padding: 40, textAlign: 'center', color: '#71717a' };
const pill = (c) => ({ background: `${c}20`, color: c, borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 });

export default AdminEngagements;
